import axios from 'axios';

const PDF_MIME = 'application/pdf';

/** Detect axios/proxy byte maps: {"0":37,"1":80,...} (%PDF) */
function isNumericByteMap(data) {
  return Boolean(
    data
    && typeof data === 'object'
    && !Array.isArray(data)
    && data.type !== 'Buffer'
    && data['0'] !== undefined
    && typeof data['0'] === 'number'
  );
}

function byteMapToUint8Array(data) {
  const keys = Object.keys(data).filter((k) => /^\d+$/.test(k));
  keys.sort((a, b) => Number(a) - Number(b));
  return new Uint8Array(keys.map((k) => data[k]));
}

function looksLikePdfMagic(bytes) {
  if (!bytes || bytes.length < 4) return false;
  return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46; // %PDF
}

function sanitizeErrorMessage(raw) {
  if (raw == null) return 'Failed to load PDF';
  if (typeof raw !== 'string') {
    if (isNumericByteMap(raw)) return 'Failed to load PDF';
    if (raw.message || raw.error) return String(raw.message || raw.error);
    return 'Failed to load PDF';
  }
  const trimmed = raw.trim();
  if (!trimmed) return 'Failed to load PDF';
  // Never surface binary / byte-map dumps in the UI
  if (trimmed.startsWith('%PDF') || trimmed.startsWith('{') && /"0"\s*:\s*37/.test(trimmed)) {
    return 'Failed to load PDF';
  }
  if (trimmed.length > 240) return `${trimmed.slice(0, 240)}…`;
  return trimmed;
}

async function messageFromBlobOrBuffer(data) {
  try {
    if (isNumericByteMap(data)) {
      // PDF (or other binary) mis-parsed as object — not a useful error body
      if (looksLikePdfMagic(byteMapToUint8Array(data))) {
        return 'Failed to load PDF';
      }
      return 'Failed to load PDF';
    }

    let text;
    if (data instanceof Blob) {
      text = await data.text();
    } else if (data instanceof ArrayBuffer) {
      text = new TextDecoder().decode(data);
    } else if (ArrayBuffer.isView(data)) {
      text = new TextDecoder().decode(data);
    } else if (typeof data === 'string') {
      text = data;
    } else if (data && typeof data === 'object') {
      return sanitizeErrorMessage(data.message || data.error || 'Failed to load PDF');
    } else {
      return 'Failed to load PDF';
    }

    if (text.startsWith('%PDF')) return 'Failed to load PDF';

    try {
      const parsed = JSON.parse(text);
      if (isNumericByteMap(parsed)) return 'Failed to load PDF';
      return sanitizeErrorMessage(parsed.message || parsed.error || 'Failed to load PDF');
    } catch {
      return sanitizeErrorMessage(text);
    }
  } catch {
    return 'Failed to load PDF';
  }
}

/**
 * Reconstruct a PDF Blob when a proxy/axios turns binary into a JSON byte map
 * e.g. {"0":37,"1":80,"2":68,"3":70,...} (%PDF...)
 */
export function coercePdfBlob(data, contentType = PDF_MIME) {
  if (data instanceof Blob) {
    return data.type ? data : new Blob([data], { type: contentType });
  }
  if (data instanceof ArrayBuffer) {
    return new Blob([data], { type: contentType });
  }
  if (ArrayBuffer.isView(data)) {
    return new Blob([data], { type: contentType });
  }
  if (data?.type === 'Buffer' && Array.isArray(data.data)) {
    return new Blob([new Uint8Array(data.data)], { type: contentType });
  }
  if (Array.isArray(data)) {
    return new Blob([new Uint8Array(data)], { type: contentType });
  }
  if (isNumericByteMap(data)) {
    return new Blob([byteMapToUint8Array(data)], { type: contentType });
  }
  throw new Error('Unexpected PDF response format');
}

async function assertPdfBlob(blob) {
  const headerText = await new Response(blob.slice(0, 5)).text();
  if (!headerText.startsWith('%PDF')) {
    throw new Error(await messageFromBlobOrBuffer(blob));
  }
  return new Blob([blob], { type: PDF_MIME });
}

/**
 * If body is already a PDF, return it even when Content-Type is wrong
 * (some proxies label binary as application/json).
 */
async function tryCoercePdfFromAnyBody(data) {
  try {
    const blob = coercePdfBlob(data, PDF_MIME);
    return await assertPdfBlob(blob);
  } catch {
    return null;
  }
}

/**
 * Fetch a PDF with axios auth (Bearer from axios defaults / localStorage).
 * Uses arraybuffer to avoid corrupt blob downloads through some proxies.
 */
export async function fetchAuthenticatedPdfBlob(url) {
  let response;
  try {
    response = await axios.get(url, {
      responseType: 'arraybuffer',
      skipGlobalErrorToast: true,
      transformResponse: [(data) => data],
      headers: {
        Accept: PDF_MIME,
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`
      }
    });
  } catch (error) {
    // Some gateways return PDF bytes with a non-2xx status — still try to open
    if (error.response?.data != null) {
      const recovered = await tryCoercePdfFromAnyBody(error.response.data);
      if (recovered) return recovered;
      throw new Error(await messageFromBlobOrBuffer(error.response.data));
    }
    throw error;
  }

  const contentType = (response.headers['content-type'] || '').toLowerCase();
  const recovered = await tryCoercePdfFromAnyBody(response.data);
  if (recovered) return recovered;

  if (contentType.includes('application/json') || contentType.includes('text/html')) {
    throw new Error(await messageFromBlobOrBuffer(response.data));
  }

  const blob = coercePdfBlob(response.data, contentType.includes('pdf') ? contentType : PDF_MIME);
  return assertPdfBlob(blob);
}

export function openPdfBlobInNewTab(blob) {
  const url = URL.createObjectURL(blob);
  const popup = window.open(url, '_blank', 'noopener,noreferrer');
  if (!popup) {
    URL.revokeObjectURL(url);
    throw new Error('Pop-up blocked. Allow pop-ups for this site to view the PDF.');
  }
  setTimeout(() => URL.revokeObjectURL(url), 120000);
  return popup;
}

export function downloadPdfBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'document.pdf');
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Delayed revoke — immediate revoke cancels the download in some browsers
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function viewAuthenticatedPdf(url) {
  const blob = await fetchAuthenticatedPdfBlob(url);
  openPdfBlobInNewTab(blob);
}

/**
 * Fetch an authenticated file (PDF or image) for in-browser preview.
 */
export async function fetchAuthenticatedFileBlob(url, fallbackMimeType = 'application/octet-stream') {
  let response;
  try {
    response = await axios.get(url, {
      responseType: 'arraybuffer',
      skipGlobalErrorToast: true,
      transformResponse: [(data) => data],
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`
      }
    });
  } catch (error) {
    if (error.response?.data != null) {
      const recovered = await tryCoercePdfFromAnyBody(error.response.data);
      if (recovered) return recovered;
      throw new Error(await messageFromBlobOrBuffer(error.response.data));
    }
    throw error;
  }

  const contentType = (response.headers['content-type'] || fallbackMimeType).split(';')[0].trim();
  if (contentType.includes('pdf') || contentType.includes('octet-stream')) {
    const recovered = await tryCoercePdfFromAnyBody(response.data);
    if (recovered) return recovered;
  }
  if (contentType.includes('application/json') || contentType.includes('text/html')) {
    throw new Error(await messageFromBlobOrBuffer(response.data));
  }

  return coercePdfBlob(response.data, contentType || fallbackMimeType);
}

export async function viewAuthenticatedFile(url, fallbackMimeType) {
  const blob = await fetchAuthenticatedFileBlob(url, fallbackMimeType);
  openPdfBlobInNewTab(blob);
}

export async function downloadAuthenticatedPdf(url, filename) {
  const blob = await fetchAuthenticatedPdfBlob(url);
  downloadPdfBlob(blob, filename);
}

/** Fetch any authenticated upload (PDF/image/etc.) and force a browser download. */
export async function downloadAuthenticatedFile(url, filename, fallbackMimeType) {
  const blob = await fetchAuthenticatedFileBlob(url, fallbackMimeType);
  downloadPdfBlob(blob, filename || 'document');
}

/** Open PDF in a new tab via API URL + JWT query token (same pattern as store challan PDFs). */
export function openAuthenticatedPdfViaToken(urlPath) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const sep = urlPath.includes('?') ? '&' : '?';
  const popup = window.open(
    `${urlPath}${sep}token=${encodeURIComponent(token)}`,
    '_blank',
    'noopener,noreferrer'
  );
  if (!popup) {
    throw new Error('Pop-up blocked. Allow pop-ups for this site to view the PDF.');
  }
}
